import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext.jsx'
import { auth } from '../config/firebase.js'
import { updateProfile } from 'firebase/auth'
import {
  COMMUNICATIONS_CONSENT_TEXT,
  buildConsentRecord,
  isPhoneValid,
} from '../constants/consent.js'
import './CompleteProfileModal.css'

/**
 * One-time blocking prompt for accounts created through an OAuth provider.
 *
 * Google gives us a display name and email but never a phone number, and those users
 * never submit the signup form — so they'd otherwise have no phone and no consent
 * record. This collects both once, then clears the flag.
 *
 * Deliberately NOT dismissable: no close button, no Escape, no backdrop click. It only
 * renders when `userProfile.needsProfileCompletion` is true, which is written once at
 * account creation (see ensureUserProfile), so existing accounts never see it.
 */
function splitDisplayName(displayName) {
  const parts = String(displayName || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { first: '', last: '' }
  if (parts.length === 1) return { first: parts[0], last: '' }
  // Everything after the first token is the surname — closer for names like
  // "Ada King Lovelace" than splitting on the last space.
  return { first: parts[0], last: parts.slice(1).join(' ') }
}

function CompleteProfileModal() {
  const { user, userProfile, updateUserProfile } = useAuth()
  const prefill = splitDisplayName(user?.displayName)

  const [firstName, setFirstName] = useState(prefill.first)
  const [lastName, setLastName] = useState(prefill.last)
  const [phone, setPhone] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  if (!userProfile?.needsProfileCompletion) return null

  const isValid = firstName.trim() && lastName.trim() && isPhoneValid(phone)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isValid || saving) return

    setSaving(true)
    setError('')

    try {
      const first = firstName.trim()
      const last = lastName.trim()

      await updateUserProfile({
        firstName: first,
        lastName: last,
        phone: phone.trim(),
        communicationsConsent: buildConsentRecord(),
        needsProfileCompletion: false,
      })

      // Keep the auth record's displayName in step with what they entered.
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, { displayName: `${first} ${last}`.trim() })
      }
    } catch (err) {
      console.error('Failed to save profile details:', err)
      setError('Could not save your details. Please try again.')
      setSaving(false)
    }
  }

  return (
    <div className="cpm-overlay">
      <div className="cpm-modal" role="dialog" aria-modal="true" aria-labelledby="cpm-title">
        <h2 className="cpm-title" id="cpm-title">Finish setting up your account</h2>
        <p className="cpm-sub">We just need a few details before you get started.</p>

        <form onSubmit={handleSubmit}>
          <div className="cpm-row">
            <div className="cpm-field">
              <label className="cpm-label" htmlFor="cpm-first">First name</label>
              <input
                id="cpm-first"
                className="cpm-input"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                autoComplete="given-name"
              />
            </div>
            <div className="cpm-field">
              <label className="cpm-label" htmlFor="cpm-last">Last name</label>
              <input
                id="cpm-last"
                className="cpm-input"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter your last name"
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="cpm-field">
            <label className="cpm-label" htmlFor="cpm-phone">Phone</label>
            <input
              id="cpm-phone"
              className="cpm-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your phone number"
              autoComplete="tel"
            />
          </div>

          {error && <div className="cpm-error">{error}</div>}

          <button type="submit" className="cpm-submit" disabled={!isValid || saving}>
            {saving ? 'Saving...' : 'Continue'}
          </button>

          <p className="cpm-consent">{COMMUNICATIONS_CONSENT_TEXT}</p>
        </form>
      </div>
    </div>
  )
}

export default CompleteProfileModal
